import { ClockIcon, CoffeeIcon, LogOutIcon, MapPin, ClockPlus, ChevronDownIcon, ChartNetwork } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";

import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label"
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input";
import type { CombinedUser } from "@/lib/collections/workerProfile-Users/getUserWithProfile";
import { updateWorkBreak, updateWorkSession } from "@/lib/collections/datapicker/updateSessionsAndBreaks";
import { toast } from "react-toastify";
import MapboxMap, { parseCoords } from "@/ubication/mapbox";
import NotesDialog from "@/assistance/notesDialog";

type SessionEvent = {
    tipo: string;
    id: string;
    sessionId?: string;
    label: string;
    hora: string;
    horaRaw: Date;
    lugar: string;
    startTime: string;
    endTime: string;
    startRaw?: Date;
    endRaw?: Date;
};

//para obtener el icono segun su tipo :P
const getIcon = (tipo: string, className: string = "w-4 h-4 text-black dark:text-white") => {
    switch (tipo) {
        case "clockIn":
            return <ClockPlus className={className} />;
        case "clockOut":
            return <LogOutIcon className={className} />;
        case "break":
            return <CoffeeIcon className={className} />;
        default:
            return <ClockIcon className={className} />;
    }
};

type WorkLogProps = {
    user: CombinedUser;
    sessionsWithBreaks: any[];
    onDateChange: (date: Date) => void;
    selectedDate: Date;
};

const WorkLog = ({ user, sessionsWithBreaks, onDateChange, selectedDate }: WorkLogProps) => {
    const [date, _setDate] = useState<Date | undefined>(new Date());
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const [eventosEditados, setEventosEditados] = useState<SessionEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);



    const sessionOfTheDay = sessionsWithBreaks.find((session) => {
        const sessionDate = new Date(session.clockIn);
        const selectedDate = new Date(selectedDateStr + 'T00:00:00');

        return sessionDate.getFullYear() === selectedDate.getFullYear() &&
            sessionDate.getMonth() === selectedDate.getMonth() &&
            sessionDate.getDate() === selectedDate.getDate();
    });

    //darle formato a la hora de los input al editar en el sheet
    const formatToTimeInput = (dateString: string) => {
        const date = new Date(dateString);
        return date.toTimeString().split(" ")[0]; // devuelve "HH:mm:ss"
    };

    // Construir eventos iniciales con tipo explícito
    const eventos: SessionEvent[] = [];
    if (sessionOfTheDay) {
        // Evento de Clock In
        eventos.push({
            tipo: "clockIn",
            id: sessionOfTheDay.$id,
            label: "Clock In",
            hora: new Date(sessionOfTheDay.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            horaRaw: new Date(sessionOfTheDay.clockIn),
            lugar: sessionOfTheDay.clockInLocation || "No especificado",
            startTime: formatToTimeInput(sessionOfTheDay.clockIn),
            endTime: ""
        });

        // Agregar breaks (si existen)
        if (sessionOfTheDay.breaks?.length) {
            sessionOfTheDay.breaks.forEach((b: any, idx: number) => {
                eventos.push({
                    tipo: "break",
                    id: b.$id,
                    sessionId: sessionOfTheDay.$id,
                    label: `Break ${idx + 1}`,
                    hora: `${new Date(b.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${b.endTime ? new Date(b.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Activo"}`,
                    horaRaw: new Date(b.startTime),
                    startRaw: new Date(b.startTime),
                    endRaw: b.endTime ? new Date(b.endTime) : undefined,
                    lugar: b.location || "No especificado",
                    startTime: formatToTimeInput(b.startTime),
                    endTime: b.endTime ? formatToTimeInput(b.endTime) : "",
                });
            });
        }

        // Evento de Clock Out (si existe)
        if (sessionOfTheDay.clockOut) {
            eventos.push({
                tipo: "clockOut",
                id: sessionOfTheDay.$id,
                label: "Clock Out",
                hora: new Date(sessionOfTheDay.clockOut).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
                horaRaw: new Date(sessionOfTheDay.clockOut),
                lugar: sessionOfTheDay.clockOutLocation || "No especificado",
                startTime: formatToTimeInput(sessionOfTheDay.clockOut),
                endTime: ""
            });
        }
    }

    // Inicializar eventos editados
    useEffect(() => {
        setEventosEditados([...eventos]);
    }, [sessionOfTheDay]);

    // Manejar cambios en los inputs de tiempo
    const handleChangeHora = (index: number, campo: 'startTime' | 'endTime', valor: string) => {
        const nuevosEventos = [...eventosEditados];
        nuevosEventos[index] = {
            ...nuevosEventos[index],
            [campo]: valor
        };
        setEventosEditados(nuevosEventos);
    };

    // Guardar cambios en la base de datos
    const handleSaveChanges = async () => {
        if (!sessionOfTheDay) return;
        setIsLoading(true);

        try {
            for (const evento of eventosEditados) {
                // Función para crear fecha completa combinando fecha + hora
                const fullDate = (time: string) =>
                    time ? new Date(`${selectedDateStr}T${time}`).toISOString() : "";

                if (evento.tipo === "clockIn") {
                    await updateWorkSession(evento.id, {
                        clockIn: fullDate(evento.startTime)
                    });
                }
                else if (evento.tipo === "clockOut") {
                    await updateWorkSession(evento.id, {
                        clockOut: fullDate(evento.startTime)
                    });
                }
                else if (evento.tipo === "break") {
                    await updateWorkBreak(evento.id, {
                        startTime: fullDate(evento.startTime),
                        endTime: evento.endTime ? fullDate(evento.endTime) : null
                    });
                }
            }
            toast.success("Cambios guardados exitosamente!")
            // Aquí deberías recargar los datos o actualizar el estado
        } catch (error) {
            console.error("Error al guardar cambios:", error);
            toast.error("Hubo un error al guardar los cambios");
        } finally {
            setIsLoading(false);
        }
    };

    //Realt time del tutorial

    // useEffect(()=>{
    //     const channel = 'databases.notesApp.collections.note.documents'
    //     const unsubscribe = client.suscribe(channel, (response)=>{
    //         const eventType = response.events[0]
    //         console.log(response.events)
    //         const changedNote = response.payload as Note 
    //         if(eventType.include('create')) {
    //             setNotes((prevNotes)=>[changedNote, ...prevNotes])
    //         }
    //         if(eventType.includes('delete')){
    //             setNotes((prevNotes)=>
    //                 prevNotes.filter((note)=> note.$id !== changedNote.$id)
    //             )
    //         }
    //     })
    //     return ()=> unsubscribe()
    // }, [])


    //real time adaptado

    //   useEffect(() => {
    //     const unsubscribe = client.subscribe(
    //       [
    //         `databases.${import.meta.env.VITE_DATABASE_ID}.collections.${import.meta.env.VITE_WORK_SESSIONS_COLLECTION_ID}.documents`,
    //         `databases.${import.meta.env.VITE_DATABASE_ID}.collections.${import.meta.env.VITE_WORK_BREAKS_COLLECTION_ID}.documents`,
    //       ],
    //       (response) => {
    //         console.log("Realtime event:", response);
    //       }
    //     );

    //     return () => {
    //       unsubscribe(); // 
    //     };
    //   }, [user]);
    if (!user) {
        return <div className="text-center p-8">Usuario no encontrado</div>;
    }

    return (

        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <ChartNetwork />
                        <BreadcrumbLink asChild>
                            <BreadcrumbPage>WorkLog</BreadcrumbPage>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage >{user.name}</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="py-6 pb-24">
                <Card className="w-full max-w-full overflow-hidden shadow-lg">
                    <CardContent className="p-4">
                        <div className="mt-2 flex flex-col lg:flex-row gap-6 items-start">
                            {/* parte izquierda de la card */}
                            <div className="w-full lg:w-2/5 flex flex-col items-center lg:items-start gap-6">
                                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 w-full">
                                    <img
                                        src={user.avatarUrl}
                                        className="w-17 h-17 rounded-full object-cover shrink-0"
                                        alt=""
                                    />
                                    <div className="text-center sm:text-left w-full wrap-break-word">
                                        <h1 className="text-base font-medium font-outfit">{user.name}</h1>
                                        {user.profile ? (
                                            <p className="text-gray-500 text-xs font-nunito wrap-break-word">
                                                {user.profile.rolCorp} <span>-{user.profile.colabCorpCode}</span>
                                            </p>
                                        ) : (
                                            <p className="text-gray-400 text-xs italic">No asignado</p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="lg:ml-20">
                                        {/* Calendario */}
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    onDateChange(date);
                                                }
                                            }}
                                            className="rounded-md border shadow-sm text-gray-400 w-full"
                                            captionLayout="dropdown"
                                        />

                                    </div>
                                </div>
                            </div>
                            {/* el road map de la jornada(cotando breaks ) */}
                            <div className="w-full max-w-full lg:w-3/5 font-outfit border rounded-lg overflow-auto h-[60vh] box-border">
                                {/* Sheet para modificar las horas de la jornada y breaks  */}
                                <div className="flex justify-end gap-2 w-full mb-4 px-4 mt-4">
                                    {/* Botón de Notas - solo mostrar si hay notas para la sesión */}
                                    {sessionOfTheDay && (
                                        <NotesDialog
                                            sessionId={sessionOfTheDay.$id}
                                            userName={user.name}
                                            selectedDate={selectedDate}
                                        />
                                    )}
                                    <Sheet >
                                        {/* <SheetTrigger asChild>
                                            <Button variant="outline" className="cursor-pointer ">Editar<SquarePen /></Button>
                                        </SheetTrigger> */}
                                        <SheetContent side="right" className="overflow-y-auto max-h-screen py-2 overflow-x-auto">
                                            <SheetHeader>
                                                <SheetTitle className="text-2xl">Editar Sesión</SheetTitle>
                                                <SheetDescription className="text-xs">
                                                    {user.name}
                                                </SheetDescription>
                                                <Separator />
                                                <div className="grid cols-3 auto-rows-min gap-6 text-black dark:text-white overflow-hidden font-nunito">
                                                    <div className="grid grid-col-2 gap-2">
                                                        <div className="space-y-10">
                                                            {eventosEditados.map((evento, idx) => (
                                                                <div key={idx} className="flex items-start gap-4 relative flex-wrap">
                                                                    {/* Ícono */}
                                                                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-black dark:bg-white">
                                                                        {getIcon(evento.tipo, "w-5 h-5 text-white dark:text-black")}
                                                                    </div>
                                                                    <Label className="text-lg">{evento.label}</Label>

                                                                    {/* Inputs dinámicos */}
                                                                    <div className="flex flex-col-1 gap-1">
                                                                        <div className="flex gap-2">
                                                                            {/* Fecha deshabilitada */}
                                                                            <div className=" flex-col gap-3 hidden">
                                                                                <Label className="px-1 text-gray-500">
                                                                                    Fecha:
                                                                                </Label>
                                                                                <Button
                                                                                    variant="outline"
                                                                                    disabled
                                                                                    className="w-25 justify-between font-normal"
                                                                                >
                                                                                    {date?.toLocaleDateString() || "Select date"}
                                                                                    <ChevronDownIcon />
                                                                                </Button>
                                                                            </div>

                                                                            {/* inputs según tipo de evento */}
                                                                            {evento.tipo === "break" ? (
                                                                                <>
                                                                                    <div className="flex flex-col gap-3">
                                                                                        <Label className="px-1 text-gray-500">
                                                                                            Hora inicial:
                                                                                        </Label>
                                                                                        <Input
                                                                                            type="time"
                                                                                            step="1"
                                                                                            value={evento.startTime}
                                                                                            onChange={(e) => handleChangeHora(idx, 'startTime', e.target.value)}
                                                                                            className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="flex flex-col gap-3">
                                                                                        <Label className="px-1 text-gray-500">
                                                                                            Hora final:
                                                                                        </Label>
                                                                                        <Input
                                                                                            type="time"
                                                                                            step="1"
                                                                                            value={evento.endTime}
                                                                                            onChange={(e) => handleChangeHora(idx, 'endTime', e.target.value)}
                                                                                            className="bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                                                        />
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="flex flex-col gap-3">
                                                                                    <Label className="px-1 text-gray-500">
                                                                                        Hora:
                                                                                    </Label>
                                                                                    <Input
                                                                                        type="time"
                                                                                        step="1"
                                                                                        value={evento.startTime}
                                                                                        onChange={(e) => handleChangeHora(idx, 'startTime', e.target.value)}
                                                                                        className="bg-background"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </SheetHeader>
                                            <SheetFooter>
                                                <Button
                                                    onClick={handleSaveChanges}
                                                    disabled={isLoading}
                                                    className="cursor-pointer"
                                                >
                                                    {isLoading ? "Guardando..." : "Guardar cambios"}
                                                </Button>
                                                <SheetClose asChild>
                                                    <Button variant="outline" className="cursor-pointer">Cancelar</Button>
                                                </SheetClose>
                                            </SheetFooter>
                                        </SheetContent>
                                    </Sheet>
                                </div>

                                {eventos.length === 0 && (
                                    <p className="text-muted-foreground">No hay sesiones para la fecha seleccionada.</p>
                                )}


                                {/* Roadmap de la jornada */}
                                <div className="relative pl-6">

                                    <div className="space-y-10">
                                        {eventos.map((evento, idx) => {
                                            // Declaramos coords fuera del try
                                            let coords: [number, number];

                                            //Intentamos parsear o fallback si algo falla
                                            try {
                                                coords = parseCoords(evento.lugar);
                                            } catch (e) {
                                                console.warn("Error parseando coords:", e);
                                                coords = [0, 0];
                                            }

                                            return (
                                                <div key={idx} className="relative flex items-start gap-4">
                                                    {idx !== eventos.length - 1 && (
                                                        <div className="absolute left-4 h-full top-9 border-l-2 bg-black dark:bg-gray-500" />
                                                    )}
                                                    <div className="absolute left-0 top-0 w-9 h-9 flex items-center justify-center rounded-full bg-black dark:bg-gray-600 shadow">
                                                        {getIcon(evento.tipo, "w-4 h-4 text-white dark:text-white")}
                                                    </div>
                                                    <div className="ml-15">
                                                        <h2 className="text-base font-semibold text-foreground pt-1.5">{evento.label}</h2>
                                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                                            <ClockIcon className="w-4 h-4" />
                                                            <span className="text-gray-600 dark:text-gray-300">{evento.hora}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-primary mt-1">
                                                            <MapPin className="w-4 h-4 text-blue-500" />
                                                            {/* <span className="text-blue-500">{evento.lugar}</span> */}

                                                            <Dialog>
                                                                {/* <DialogTrigger asChild>
                                                                    <Link to="." className="text-blue-500">
                                                                        Ubicación
                                                                    </Link>
                                                                </DialogTrigger> */}

                                                                <DialogTrigger asChild>
                                                                    <button className="text-blue-500 dark:text-blue-400 cursor-pointer hover:underline">
                                                                        Ubicación
                                                                    </button>
                                                                </DialogTrigger>
                                                                <DialogContent className="w-full max-w-2xl">
                                                                    <DialogHeader>
                                                                        <DialogTitle>Ubicación de {evento.label}</DialogTitle>
                                                                        <DialogDescription>
                                                                            Cordenadas: <span className="text-blue-500"> {coords[1].toFixed(6)}, {coords[0].toFixed(6)}</span>                                                                         </DialogDescription>
                                                                    </DialogHeader>

                                                                    <MapboxMap coords={coords} height="h-70" />

                                                                    <DialogClose asChild>
                                                                        <Button className="mt-4 px-4 py-2 cursor-pointer">Cerrar</Button>
                                                                    </DialogClose>
                                                                </DialogContent>
                                                            </Dialog>

                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>


                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};


export default WorkLog;